import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { RECENCY_LEGEND } from '../utils/recencyColor';

interface FAQItem {
  question: string;
  answer: ReactNode;
}

/** Keyboard key / mouse chord rendered inline in an answer. */
function Key({ children }: { children: ReactNode }) {
  return (
    <kbd className="px-1.5 py-0.5 mx-0.5 rounded-sm border border-slate-700 bg-slate-800 text-slate-200 text-xs font-mono">
      {children}
    </kbd>
  );
}

const faqs: FAQItem[] = [
  {
    question: 'What is BookMyBand?',
    answer:
      'BookMyBand is a booking management tool for music venues and bands. It helps you manage contacts, venues, concerts, and email communications all in one place.',
  },
  {
    question: 'How do I connect my Gmail account?',
    answer:
      'Go to your Profile page and click "Connect Gmail". You will be redirected to Google to authorise access. BookMyBand uses Gmail read and send permissions exclusively to sync and send booking emails on your behalf.',
  },
  {
    question: 'How often does email sync run?',
    answer:
      'The inbox syncs automatically in the background on an hourly schedule. You can also trigger a manual sync at any time from the toolbar at the top of the page.',
  },
  {
    question: 'How do I add a new venue?',
    answer:
      'Open the Venues page and click "Add Venue". Fill in the venue details, including name, location, and capacity. Venues can also be linked to contacts and concerts.',
  },
  {
    question: 'Can I import existing contacts or venues?',
    answer:
      'Yes. Go to the Import page to upload a CSV file. The importer supports contacts and venues. Download the sample template on that page to see the expected column format.',
  },
  {
    question: 'How do I avoid importing a venue I already have?',
    answer:
      'The review step of a CSV import marks every row whose venue name you already have: an orange "name & city" badge when the city matches too (almost certainly the same place) and an amber "name only" badge when you have that name in another city. Hover a badge to see which venue it matched. Nothing is unticked for you — the two buttons above the table drop a whole group at once, and each one turns into "Reselect" if you change your mind, so you can flip a group on and off. The Show filter does the same job row by row: narrow to a group, tick what you want, then "Select all shown". Importing never merges, so every row left ticked becomes a new venue. A contact whose email address you already have is marked with a link icon and stays ticked on purpose — that contact is linked to the new venue rather than duplicated.',
  },
  {
    question: 'What are Mailing Lists used for?',
    answer:
      'Mailing Lists let you group contacts so you can send a single email to multiple recipients at once — useful for tour announcements, press releases, or booking outreach. Every send from a list is recorded as a campaign named after its subject, and the list page shows how far each campaign has got. Expired and do-not-contact contacts are never emailed, even if they are flagged after a campaign has started.',
  },
  {
    question: 'How do reminders work?',
    answer:
      'You can create reminders on any contact or venue. When a reminder is due, it appears in your Todo list so you never miss a follow-up. Reminders can also have custom notes attached.',
  },
  {
    question: 'Shortcuts: which keyboard and mouse shortcuts are available?',
    answer: (
      <div className="space-y-4">
        <div>
          <p className="font-medium text-white mb-2">In the contact overlay</p>
          <p className="mb-2">
            These work whenever the contact overlay is open and you are not typing in a field:
          </p>
          <ul className="space-y-1.5 list-disc pl-5">
            <li>
              <Key>P</Key> — toggle <span className="text-white">Previous Communications</span>. Same
              as clicking the header: it hides the venue details so the communication history fills
              the panel, and shows them again on a second press.
            </li>
            <li>
              <Key>T</Key> — toggle <span className="text-white">Todos</span>. Same as clicking the
              Todos header. If the todos are currently hidden because Previous Communications is
              expanded, pressing <Key>T</Key> brings the venue details back and shows the todos.
            </li>
            <li>
              <Key>S</Key> — show only the mail and calls you{' '}
              <span className="text-white">sent</span>, or everything again. Same as clicking the
              blue <span className="text-white">Sent</span> count in the Previous Communications
              header. Scheduled and failed emails stay in this view — they are on their way out —
              even though the count itself only counts what has really gone out.
            </li>
            <li>
              <Key>R</Key> — show only what you{' '}
              <span className="text-white">received</span>, or everything again. Same as clicking
              the green <span className="text-white">Received</span> count. The contact dropdown
              next to the counts still applies, so you can see, say, only what one person replied.
            </li>
            <li>
              <Key>F</Key> — flag or unflag the venue. Same as clicking the flag next to its name
              (red when flagged). On the <span className="text-white">Venues</span> page, the flag
              button next to the column settings shows only flagged venues, or only the others.
            </li>
            <li>
              <Key>←</Key> <Key>→</Key> — move to the previous or next venue without leaving the
              overlay (when you opened it from a list).
            </li>
            <li>
              <Key>Ctrl</Key>+click (<Key>⌘</Key>+click on Mac) on a message in{' '}
              <span className="text-white">Previous Communications</span> — expand or collapse its
              full text, the same as the chevron on the right of the message. It does not select
              the message as the one you are replying to, which is what a normal click does.
            </li>
            <li>
              Right-click on an email address in{' '}
              <span className="text-white">Previous Communications</span> — opens the{' '}
              <span className="text-white">New Contact</span> form with that address (and the name
              from the message headers, when there is one) already filled in. If the address already
              belongs to one of the venue's contacts, that contact is selected instead. Right-clicking
              anywhere else in a message shows the normal browser menu.
            </li>
            <li>
              Right-click on any line of the venue card — the <span className="text-white">type</span>,
              the <span className="text-white">styles</span>, the{' '}
              <span className="text-white">Contact</span>, <span className="text-white">Answer by</span>{' '}
              and <span className="text-white">Concert</span> dates, or the{' '}
              <span className="text-white">commentary</span> between them and the map — opens a small
              box to set or change just that one thing, the same box the matching column offers on the{' '}
              <span className="text-white">Venues</span> page. Every line is shown even when the venue
              has nothing in it yet — it reads <span className="text-white">Not set</span>,{' '}
              <span className="text-white">No type</span>, <span className="text-white">No styles</span> or{' '}
              <span className="text-white">No commentary</span> — so there is always somewhere to click.
              The pencil by the venue's name still opens the full form.
            </li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-white mb-2">Picking an email template</p>
          <p className="mb-2">
            The <span className="text-white">Template</span> box above the subject filters as you
            type, so you never have to scroll a long list:
          </p>
          <ul className="space-y-1.5 list-disc pl-5">
            <li>
              Click it — or just start typing letters while it is selected — and only the templates
              whose <span className="text-white">name</span> contains what you typed stay listed.
              Any part of the name works: typing <span className="text-white">exampleband</span>{' '}
              finds <span className="text-white">Rock — exampleband</span>.
            </li>
            <li>
              <Key>↑</Key> <Key>↓</Key> to move through the matches, <Key>Enter</Key> to apply the
              highlighted one, <Key>Esc</Key> to close the list and leave the overlay open.{' '}
              <span className="text-white">No template</span> at the top clears the choice — what
              you have already written stays as it is.
            </li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-white mb-2">In the inbox</p>
          <ul className="space-y-1.5 list-disc pl-5">
            <li>
              <Key>U</Key> — on the <span className="text-white">Received</span> tab, show only
              unread mail, or show everything again. Same as ticking <span className="text-white">Unread</span>{' '}
              in the filter on the right of the table. It never switches to read-only mail, and it
              does nothing while the contact overlay or another window is open, or while you are
              typing in a field.
            </li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-white mb-2">While writing an email</p>
          <p className="mb-2">
            These work inside the message box, wherever you write mail — the contact overlay, a
            mailing-list send, or a template:
          </p>
          <ul className="space-y-1.5 list-disc pl-5">
            <li>
              <Key>/</Key> — at the start of a word, opens your{' '}
              <span className="text-white">Mail Snippets</span> (Profile → Settings). Keep typing to
              filter them, by tag first and then by the sentence itself; <Key>↑</Key> <Key>↓</Key>{' '}
              to move and <Key>Enter</Key> to insert the one you want. <Key>Esc</Key> closes the
              list and leaves what you typed. Pressing <Key>Enter</Key> straight after the slash
              just starts a new line, so <Key>/</Key> is still an ordinary character, and a slash
              inside a word or a date (12/05) never opens anything.
            </li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-white mb-2">Opening the contact overlay</p>
          <p>
            On the <span className="text-white">Venues</span> and <span className="text-white">Todos</span>{' '}
            pages, <Key>Ctrl</Key>+click (<Key>⌘</Key>+click on Mac) on a row opens the contact
            overlay for that row directly, skipping the row menu that a normal click brings up.
          </p>
          <p className="mt-2">
            Inside a <span className="text-white">mailing list</span>, a member's row works the same
            way — or <span className="text-white">Open Venue</span> in the row menu. It opens the{' '}
            <span className="text-white">venue</span> the member belongs to, with every contact of
            that venue and not only the address on the list, so you can see what a colleague
            replied. <Key>←</Key> <Key>→</Key> then move through the list member by member, in the
            order the tables show them and through whatever your search has narrowed them to.
          </p>
        </div>
      </div>
    ),
  },
  {
    question: 'What do the Last Contact date and the Sent / Received counts show?',
    answer: (
      <div className="space-y-4">
        <p>
          When the venue was last <em>reached</em>, and how much mail that exchange came to — not
          simply the newest mail in its history and a total of everything on file. Only the
          venue's <span className="text-white">active</span> contacts count towards all three:
          gatekeepers, expired contacts and anyone marked do-not-contact are left out, because an
          exchange with them does not mean you got through to someone who can book you.
        </p>
        <p>
          If a venue has no active contact at all, all three fall back to its gatekeepers, and
          failing that to whoever is left. It is always the same group of people for the date and
          the two counts, so they never contradict each other. Hover the date: when it came from
          one of those fallbacks the tooltip says so under the age.
        </p>
        <p>
          Gatekeeper is decided per venue: the same town hall address can be the gatekeeper of the
          city and the active contact of its Comité des Fêtes, and each venue counts it accordingly.
        </p>
        <p>
          This is why a venue can read <span className="text-white">Never</span> with{' '}
          <span className="text-white">0</span> sent and still have emails in its history — you
          have written to the venue, but not yet to anyone reachable there. Those are usually the
          ones worth picking up. Open the venue to see every communication; nothing is hidden,
          only the summary is opinionated.
        </p>
        <p>
          A scheduled email counts as sent once it actually goes out, not when you queue it.
        </p>
        <p>
          Sorting and the filters on those columns follow the same values, so the order you see is
          the order of the dates and counts you see. The date filter's{' '}
          <span className="text-white">Never contacted</span> box pulls those{' '}
          <span className="text-white">Never</span> venues out on their own — tick it with no dates
          set — and when a From/To is set as well it keeps them <em>beside</em> the range rather
          than inside it.
        </p>
      </div>
    ),
  },
  {
    question: "How do I find a venue's booking contact?",
    answer: (
      <div className="space-y-2">
        <p>
          The magnifier next to the <span className="text-white">Select Contact</span> heading, on
          the right of the overlay, opens a Google search in a new tab for the person who books this
          venue. Hover it to see the exact search first, then use{' '}
          <span className="text-white">New</span> to save whatever address you find.
        </p>
        <p>
          For a <span className="text-white">city</span> venue it looks for the mairie's cultural
          department — <span className="text-white">contact service culturel [city]</span>. For every
          other kind of venue it looks for a programmer —{' '}
          <span className="text-white">contact email programmateur [venue] [city]</span>. The city is
          added because venue names repeat from one town to the next, and it comes from the venue's
          address, so filling that in makes the search sharper.
        </p>
      </div>
    ),
  },
  {
    question: 'Why can an email address only belong to one contact?',
    answer: (
      <div className="space-y-4">
        <p>
          Because a contact <em>is</em> its email address: its synced mail, its bounces and its
          do-not-contact choice all follow the address. Two contacts for one address would each
          hold their own copy of the same emails. So adding a contact — by hand or by CSV import —
          with an address you already have links your existing contact to the venue instead of
          creating a second one, and giving a contact an address another contact holds offers to
          merge the two.
        </p>
        <p>
          One contact can be linked to several venues, with a different role at each: the town
          hall can be the city's gatekeeper and the active contact of its Comité des Fêtes. Every
          email appears once in the Inbox, even when several of your contacts were on it.
        </p>
      </div>
    ),
  },
  {
    question: 'Why are dates shown in different colours?',
    answer: (
      <div className="space-y-4">
        <p>
          The <span className="text-white">Last Contact</span> column on the Venues page, the{' '}
          <span className="text-white">Date</span> column in the Inbox and the dates in a venue's{' '}
          <span className="text-white">Previous Communications</span> are tinted by age: a recent
          date is green and fades through yellow to red as it gets older, so a stale venue stands
          out without you reading every row. The first month stays green — nothing goes stale the
          day after you write to it — and the scale keeps darkening past a year.
        </p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {RECENCY_LEGEND.map((stop) => (
            <span key={stop.label} className="flex items-center gap-2 text-sm">
              <span
                className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: stop.color }}
              />
              <span style={{ color: stop.color }}>{stop.label}</span>
            </span>
          ))}
        </div>
        <p>
          Hover any date to see the age spelled out — <span className="text-white">12 days ago</span>,{' '}
          <span className="text-white">1 month and 3 days ago</span>,{' '}
          <span className="text-white">5 months ago</span>,{' '}
          <span className="text-white">2 years ago</span> — in as much detail as is worth having at
          that distance.
        </p>
        <p>
          A venue you have never contacted keeps the plain text colour rather than joining the
          scale, and an email still waiting to be sent stays yellow with a clock beside it.
        </p>
      </div>
    ),
  },
  {
    question: 'Is my data private?',
    answer:
      'Yes. All your data (venues, contacts, emails) is stored securely and is only accessible by your account. We do not sell or share your data with third parties. See our Terms & Conditions for full details.',
  },
  {
    question: 'How do I disconnect my Gmail account?',
    answer:
      'Go to your Profile page and click "Disconnect Gmail". This will revoke BookMyBand\'s access to your Gmail account and remove your stored OAuth credentials.',
  },
  {
    question: 'Who can I contact for support?',
    answer:
      'Reach us at hello@bookmyband.io or use the Contact page. We typically respond within one business day.',
  },
];

function FAQItemRow({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-slate-800 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left text-white hover:bg-slate-800/50 transition-colors"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="font-medium">{item.question}</span>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 text-slate-300 leading-relaxed border-t border-slate-800 pt-4">
          {item.answer}
        </div>
      )}
    </div>
  );
}

export function FAQPage() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-3xl font-bold text-white mb-2">Frequently Asked Questions</h1>
      <p className="text-slate-500 text-sm mb-10">Everything you need to know about BookMyBand.</p>

      <div className="space-y-3">
        {faqs.map((item) => (
          <FAQItemRow key={item.question} item={item} />
        ))}
      </div>
    </div>
  );
}
